import React, {useState} from 'react';
import {
    Box, CircularProgress,
    Container,
    Fab,
    FormControl,
    MenuItem,
    Select,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigationBarView from "../../navbar/components/NavigationBarView";
import WordAddView from "../components/WordAddView/WordAddView";
import {theme} from "../../../app/theme";
import {LoadingPageView} from "../../../common/components/LoadingPageView";
import LogoErrorView from "../../../common/components/LogoErrorView";
import ColoredWordListView from "../components/WordListView/components/ColoredWordListView";
import {useVocabulary, type VocabularyActionDispatch} from "../hooks/useVocabulary";

export type {VocabularyActionDispatch} from "../hooks/useVocabulary";

export function Vocabulary() {

    const [isAddingWord, setIsAddingWord] = useState(false);

    const {
        vocabulary,
        sortedUnits,
        currentUnit,
        tabIndex,
        setTabIndex,
        actionDispatch,
        error,
        isReady,
        currentUnitPending,
        hasUnits,
    } = useVocabulary();

    if (error) {
        return (
            <LogoErrorView severity={"error"} message={"An error occurred. Try refreshing the page."}/>
        )
    }

    if (!isReady) {
        return (
            <LoadingPageView message={"Loading vocabulary..."}></LoadingPageView>
        )
    }

    if (!hasUnits) {
        return (
            <Container disableGutters={true} maxWidth={false}>
                <NavigationBarView displayDeckSelectionDropdown={true} displaySearchBar={true} displayVocabularyLink={true}/>
                <Typography variant="h3" align="center" sx={{my: 3}}>Vocabulary</Typography>
                <Typography variant="body1" align="center">No units available for this deck.</Typography>
            </Container>
        );
    }

    return (
        <Container disableGutters={true} maxWidth={false} sx={{bgcolor: 'grey.50', height: '100vh', }}>
            <NavigationBarView displayDeckSelectionDropdown={true} displaySearchBar={true} displayVocabularyLink={true}/>

            {contentView()}

            {fabView()}
            {wordAddView()}

        </Container>
    )

    function contentView() {
        return (
            <Box sx={{maxWidth: 1300, mx: 'auto', mt: 3}}>
                <Box sx={{p: 2}}>
                    <FormControl fullWidth>
                        <Select
                            value={tabIndex}
                            onChange={(e) => setTabIndex(Number(e.target.value))}
                            sx={{backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText, fontWeight: 'bold', borderRadius: 2}}
                            MenuProps={{disableScrollLock: true}}
                        >
                            {sortedUnits.map((unit, index) => (
                                <MenuItem key={unit.id} value={index}>{unit.unitName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {unitView()}

            </Box>
        );
    }

    function unitView() {
        const topics = vocabulary.get(currentUnit!.id) ?? [];
        const totalWordsCount = topics.reduce((count, topic) => count + topic.words.length, 0);

        return currentUnit && (
            <Box sx={{px: 2, pb: 2}}>
                {currentUnitPending ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
                        <CircularProgress size={30} sx={{color: theme.palette.secondary.dark}}/>
                        <Typography variant="caption" sx={{color: theme.palette.text.primary, ml: 2}}>
                            Loading words for {currentUnit.unitName}...
                        </Typography>
                    </Box>
                ) : currentUnit.isCustom && totalWordsCount === 0 ? (
                    <Box sx={{mt: 2, p: 4, textAlign: 'center'}}>
                        <Typography variant="body1" color="text.secondary">Words you add will show up here</Typography>
                    </Box>
                ) : (
                    topics.map((topic, index) => (
                        <Box key={`${currentUnit.id}-${topic.topicId ?? index}`} sx={{mt: 2, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden'}}>
                            <Box sx={{backgroundColor: theme.palette.primary.light, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                                <Typography variant="h6">{topic.topicName}</Typography>
                            </Box>
                            <Box sx={{bgcolor: 'grey.200', p: 2}}>
                                <ColoredWordListView
                                    words={topic.words}
                                    colorByStages={true}
                                    stageSymbol="none"
                                    actionDispatch={actionDispatch as VocabularyActionDispatch}
                                />
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        );
    }

    function fabView() {
        return (
            <Fab title="Add word"
                 onClick={() => setIsAddingWord(true)}
                 sx={{
                     position: 'absolute',
                     bottom: 40,
                     right: 45,
                     backgroundColor: theme.palette.info.main,
                     color: theme.palette.info.contrastText,
                     '&:hover': {backgroundColor: theme.palette["purple"].dark}
                 }}>
                <AddIcon/>
            </Fab>
        )
    }

    function wordAddView() {
        return isAddingWord ? (
            <WordAddView actionDispatch={actionDispatch}
                         onDrawerClosed={() => setIsAddingWord(false)}
                         snackbar={{showRequestSentSnackbar: true, showRequestSuccessSnackbar: true, showRequestErrorSnackbar: true}}
            />
        ) : null;
    }
}